// Pure follow rules for the refresh control (C6.6). No React, no fetch: every
// transition is a reducer case so it is testable without a DOM.

import type { RefreshSource, RunRow } from "./api";
import { isStaleRun, parisTime, STALE_RUN_MS } from "./format";

export const POLL_MS = 3000;
/** Two answers in a row with no progress: the engine that ran this run has restarted (C2 D3, D6). */
export const MAX_EMPTY_POLLS = 2;
/** Ten failed polls in a row (30 s): stop and let the operator press again. */
export const MAX_FAILED_POLLS = 10;

export type FollowVerdict = "follow" | "done" | "orphaned" | "stale" | "lost";

export function followVerdict(f: { run: RunRow | null; emptyPolls: number; failedPolls: number; now: number }): FollowVerdict {
  if (f.failedPolls >= MAX_FAILED_POLLS) return "lost";
  if (!f.run) return "follow";
  if (f.run.status !== "running") return "done";
  if (isStaleRun(f.run, f.now)) return "stale";
  if (f.emptyPolls >= MAX_EMPTY_POLLS) return "orphaned";
  return "follow";
}

export function stallMessage(verdict: "orphaned" | "stale" | "lost", run: RunRow | null): string {
  if (verdict === "lost") {
    return "We lost contact with the engine while following this run. Press Refresh to check on it again.";
  }
  if (verdict === "stale") {
    return "This run has been going for over an hour, so the next press clears it. Press Refresh to start a new one.";
  }
  const started = run ? Date.parse(run.started_at) : Number.NaN;
  return Number.isNaN(started)
    ? "This run stopped reporting progress, most likely because the engine restarted. Press Refresh again shortly."
    : `This run stopped reporting progress, most likely because the engine restarted. A new run can start from ${parisTime(started + STALE_RUN_MS)} (Paris time).`;
}

export type RefreshState = "idle" | "running" | "done" | "error" | "stalled";

/** `followed` is the last run this control followed, so it is never re-attached after a stall. */
export type RefreshModel = { state: RefreshState; runId: string | null; followed: string | null; message: string };

export const IDLE: RefreshModel = { state: "idle", runId: null, followed: null, message: "" };

export type RefreshEvent =
  | { type: "press"; source?: RefreshSource }
  | { type: "started"; runId: string; started: boolean; source?: RefreshSource }
  | { type: "refused"; message: string }
  | { type: "attach"; run: RunRow }
  | { type: "verdict"; verdict: FollowVerdict; run: RunRow | null };

function pressText(source?: RefreshSource): string {
  return source === "xero" ? "Refreshing Xero stock…" : "Refreshing…";
}

export function refreshReducer(m: RefreshModel, e: RefreshEvent): RefreshModel {
  switch (e.type) {
    case "press":
      // runId is cleared BEFORE the POST: a finished run's cached answer can never end the new press.
      return { ...m, state: "running", runId: null, message: pressText(e.source) };
    case "started":
      return {
        state: "running",
        runId: e.runId,
        followed: e.runId,
        message: e.started ? pressText(e.source) : "A refresh is already running — following it.",
      };
    case "refused":
      return { ...m, state: "error", runId: null, message: e.message };
    case "attach":
      // A run already in progress is followed, not restarted; one this control gave up on is not re-followed.
      if (m.state === "running" || e.run.status !== "running" || e.run.run_id === m.followed) return m;
      return { state: "running", runId: e.run.run_id, followed: e.run.run_id, message: "A run is in progress — following it." };
    case "verdict": {
      if (m.state !== "running" || e.verdict === "follow") return m;
      if (e.verdict === "done") {
        const status = e.run?.status ?? "finished";
        return { ...m, state: "done", message: e.run?.note ? `Run ${status}: ${e.run.note}` : `Run ${status}.` };
      }
      return { ...m, state: "stalled", message: stallMessage(e.verdict, e.run) };
    }
  }
}
